import React, { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Eye,
  EyeOff,
  LogIn,
  Building,
  User,
  Shield,
  Mail,
  Lock,
  ArrowRight,
  AlertCircle,
} from "lucide-react";
import { AuthAnimation } from "@/components/auth/AuthAnimation";
import { AnimatedBackground } from "@/components/ui/AnimatedBackground";

export function LoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showAnimation, setShowAnimation] = useState(false);
  const [animationType, setAnimationType] = useState<"loading" | "login">(
    "loading",
  );
  const [formErrors, setFormErrors] = useState<{
    email?: string;
    password?: string;
  }>({});

  const { login, isAuthenticated, isLoading, error, clearError } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (error) {
      setShowAnimation(false);
    } else if (isAuthenticated) {
      setAnimationType("login");
      setShowAnimation(true);
    } else {
      setShowAnimation(false);
    }
  }, [error, isAuthenticated]);

  const handleAnimationComplete = () => {
    navigate("/index", { replace: true });
    setShowAnimation(false);
  };

  const validateForm = () => {
    const errors: { email?: string; password?: string } = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email) {
      errors.email = "El correo electrónico es requerido";
    } else if (!emailRegex.test(email)) {
      errors.email = "Ingresa un correo electrónico válido";
    }

    if (!password) {
      errors.password = "La contraseña es requerida";
    } else if (password.length < 6) {
      errors.password = "La contraseña debe tener al menos 6 caracteres";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      await login({
        email: email.trim(),
        password: password,
        rememberMe,
      });
    } catch (err: any) {
      console.error("Login error:", err);

      let errorMessage =
        err.response?.data?.message ||
        err.message ||
        "Error al iniciar sesión.";

      setFormErrors((prev) => ({
        ...prev,
        email: errorMessage,
      }));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === "email") setEmail(value);
    if (name === "password") setPassword(value);

    if (formErrors[name as "email" | "password"]) {
      setFormErrors((prev) => ({ ...prev, [name]: undefined }));
    }

    if (error) {
      clearError();
    }
  };

  const handleForgotPassword = () => {
    navigate("/forgot-password");
  };

  const handleNoAccount = () => {
    navigate("/register");
  };

  // Removed redundant useEffect

  if (showAnimation) {
    return (
      <AuthAnimation
        isOpen={true}
        type={animationType}
        onComplete={handleAnimationComplete}
      />
    );
  }

  // No longer needed, as we navigate immediately

  return (
    <div className="min-h-screen overflow-hidden relative">
      <AnimatedBackground />

      <div className="relative z-10 flex items-center justify-center min-h-screen px-4 py-4">
        <div className="w-full max-w-sm">

          <motion.div
            className="flex flex-col items-center justify-center space-y-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-center space-y-4 w-full animate-fade-in-up">
              <div className="space-y-2">
                <h1
                  className="text-3xl md:text-4xl font-black tracking-tight"
                  style={{
                    background:
                      "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  ProGraphic
                </h1>
                <p
                  className="text-lg font-medium max-w-md mx-auto"
                  style={{
                    color: "#64748b",
                  }}
                >
                  Potenciando tu negocio con tecnología de vanguardia
                </p>
              </div>
            </div>

            {(error || formErrors.email || formErrors.password) && (
              <Alert
                variant="destructive"
                className="w-full max-w-md animate-fade-in-up"
                style={{
                  backgroundColor: "rgba(239, 68, 68, 0.1)",
                  borderColor: "rgba(239, 68, 68, 0.2)",
                }}
              >
                <AlertCircle className="h-4 w-4" style={{ color: "#ef4444" }} />
                <AlertDescription style={{ color: "#ef4444" }}>
                  {error || formErrors.email || formErrors.password}
                </AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-4">
              <div className="space-y-2">
                <Label
                  className="text-sm font-medium"
                  style={{
                    color: "#334155",
                  }}
                >
                  Correo Electrónico
                </Label>
                <div className="relative group">
                  <div
                    className="absolute inset-0 rounded-xl blur group-focus-within:blur-md transition-all duration-300"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)",
                    }}
                  />
                  <div
                    className="relative bg-white/80 backdrop-blur-md rounded-xl p-2.5 flex items-center gap-3 border"
                    style={{
                      borderColor: "rgba(59, 130, 246, 0.2)",
                    }}
                  >
                    <Mail className="w-5 h-5" style={{ color: "#3b82f6" }} />
                    <input
                      name="email"
                      type="email"
                      value={email}
                      onChange={handleInputChange}
                      placeholder="administrador@ejemplo.com"
                      className="flex-1 bg-transparent outline-none placeholder:text-gray-400"
                      style={{
                        color: "#1e293b",
                      }}
                      required
                      disabled={isLoading}
                      autoComplete="email"
                    />
                  </div>
                </div>
                {formErrors.email && (
                  <p className="text-sm mt-1" style={{ color: "#ef4444" }}>
                    {formErrors.email}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label
                  className="text-sm font-medium"
                  style={{
                    color: "#334155",
                  }}
                >
                  Contraseña
                </Label>
                <div className="relative group">
                  <div
                    className="absolute inset-0 rounded-xl blur group-focus-within:blur-md transition-all duration-300"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(59, 130, 246, 0.2) 100%)",
                    }}
                  />
                  <div
                    className="relative bg-white/80 backdrop-blur-md rounded-xl p-2.5 flex items-center gap-3 border"
                    style={{
                      borderColor: "rgba(139, 92, 246, 0.2)",
                    }}
                  >
                    <Lock className="w-5 h-5" style={{ color: "#8b5cf6" }} />
                    <input
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={handleInputChange}
                      placeholder="••••••••"
                      className="flex-1 bg-transparent outline-none placeholder:text-gray-400"
                      style={{
                        color: "#1e293b",
                      }}
                      required
                      disabled={isLoading}
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="transition-colors"
                      style={{ color: "#94a3b8" }}
                      disabled={isLoading}
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5 hover:text-blue-500" />
                      ) : (
                        <Eye className="w-5 h-5 hover:text-blue-500" />
                      )}
                    </button>
                  </div>
                </div>
                {formErrors.password && (
                  <p className="text-sm mt-1" style={{ color: "#ef4444" }}>
                    {formErrors.password}
                  </p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="rememberMe"
                    checked={rememberMe}
                    onCheckedChange={(checked) =>
                      setRememberMe(checked as boolean)
                    }
                    className="border-gray-300"
                    style={{
                      borderColor: "#cbd5e1",
                    }}
                    disabled={isLoading}
                  />
                  <Label
                    htmlFor="rememberMe"
                    className="text-sm cursor-pointer"
                    style={{
                      color: "#475569",
                    }}
                  >
                    Recordarme
                  </Label>
                </div>

                <Button
                  type="button"
                  variant="link"
                  className="text-sm p-0 h-auto font-medium"
                  style={{
                    color: "#3b82f6",
                  }}
                  disabled={isLoading}
                  onClick={handleForgotPassword}
                >
                  ¿Olvidaste tu contraseña?
                </Button>
              </div>

              <button
                type="submit"
                disabled={isLoading || !email || !password}
                className="w-full relative group overflow-hidden rounded-xl"
              >
                <div
                  className="absolute inset-0 group-hover:scale-105 transition-transform duration-300"
                  style={{
                    background:
                      "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                  }}
                />
                <div
                  className="relative px-6 py-2.5 font-semibold flex items-center justify-center gap-2 hover:shadow-lg transition-all duration-300"
                  style={{
                    color: "white",
                  }}
                >
                  {isLoading ? (
                    <>
                      <div
                        className="w-5 h-5 border-2 rounded-full animate-spin"
                        style={{
                          borderColor: "rgba(255, 255, 255, 0.3)",
                          borderTopColor: "white",
                        }}
                      />
                      Iniciando sesión...
                    </>
                  ) : (
                    <>
                      <LogIn className="w-5 h-5" />
                      Iniciar Sesión
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </div>
              </button>
            </form>

            <div className="w-full max-w-sm">
              <p
                className="text-center text-sm"
                style={{
                  color: "#64748b",
                }}
              >
                ¿No tienes una cuenta?{" "}
                <Button
                  type="button"
                  variant="link"
                  className="p-0 h-auto text-sm font-medium"
                  style={{
                    color: "#3b82f6",
                  }}
                  onClick={handleNoAccount}
                >
                  Registrarse
                </Button>
              </p>
            </div>

            <div className="text-center">
              <p
                className="text-xs flex items-center justify-center"
                style={{
                  color: "#64748b",
                }}
              >
                <Shield className="h-3 w-3 mr-1" style={{ color: "#3b82f6" }} />
                Tu información está protegida y encriptada
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
